import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Phone, Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { sendContact } from "../lib/api";
import { ABOUT_HOME_IMAGE } from "../constants/assets";
import { CONTACT_ADDRESS, CONTACT_EMAIL, CONTACT_PHONE } from "../constants/contact";

function ContactLine({ icon: Icon, children, href }) {
  const inner = (
    <>
      <span className="w-9 h-9 shrink-0 rounded-lg bg-[rgba(139,92,246,0.12)] border border-[rgba(168,85,247,0.22)] flex items-center justify-center">
        <Icon size={16} className="text-[var(--tv-purple-bright)]" />
      </span>
      <span className="text-sm text-white/90 leading-snug">{children}</span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className="flex items-center gap-3 p-3 rounded-xl border border-transparent hover:border-[rgba(168,85,247,0.25)] hover:bg-[rgba(139,92,246,0.06)] transition-colors"
      >
        {inner}
      </a>
    );
  }

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-transparent">
      {inner}
    </div>
  );
}

export default function HomeAboutContact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await sendContact(form);
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus("idle"), 3500);
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="home-bottom-strip tv-fade-in">
      <div className="home-bottom-strip-inner">
        <div className="grid lg:grid-cols-12 min-h-0">
          {/* Hakkımızda */}
          <div className="lg:col-span-5 flex flex-col border-b lg:border-b-0 lg:border-r border-[var(--tv-border)]">
            <div className="relative h-40 sm:h-44 lg:h-52 shrink-0">
              <img
                src={ABOUT_HOME_IMAGE}
                alt="TV13 stüdyo"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c14] via-[#0b0c14]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <span className="tv-badge text-[10px] mb-2">Kurumsal</span>
                <h3 className="text-xl md:text-2xl font-bold text-white font-display">Hakkımızda</h3>
              </div>
            </div>
            <div className="p-5 md:p-6 flex flex-col flex-1">
              <p className="text-sm text-[var(--tv-muted)] leading-relaxed">
                TV13, haberden spora, kültürden sanata hayatın her alanına dair güvenilir içerikler
                sunan bir yayın platformudur.
              </p>
              <Link
                to="/hakkimizda"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--tv-purple-bright)] hover:text-white transition-colors group"
              >
                Daha fazla bilgi
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {/* Bize Ulaşın */}
          <div className="lg:col-span-7 p-5 md:p-6 lg:p-7">
            <h3 className="text-xl md:text-2xl font-bold text-white font-display flex items-center gap-3">
              <span className="tv-section-bar shrink-0" />
              Bize Ulaşın
            </h3>
            <p className="text-sm text-[var(--tv-muted)] mt-2 mb-5">
              Sorularınız için formu doldurun veya doğrudan iletişime geçin.
            </p>

            <div className="grid md:grid-cols-5 gap-5 md:gap-6">
              <div className="md:col-span-2 space-y-1">
                <ContactLine icon={Phone} href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}>
                  {CONTACT_PHONE}
                </ContactLine>
                <ContactLine icon={Mail} href={`mailto:${CONTACT_EMAIL}`}>
                  {CONTACT_EMAIL}
                </ContactLine>
                <ContactLine icon={MapPin}>{CONTACT_ADDRESS}</ContactLine>
              </div>

              <form onSubmit={submit} className="md:col-span-3 space-y-2.5">
                <div className="grid sm:grid-cols-2 gap-2.5">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Adınız Soyadınız"
                    className="tv-input !py-2.5 !text-sm"
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="E-posta Adresiniz"
                    className="tv-input !py-2.5 !text-sm"
                  />
                </div>
                <textarea
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Mesajınız"
                  rows={3}
                  className="tv-input resize-none !py-2.5 !text-sm min-h-[88px]"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="tv-btn-primary w-full sm:w-auto !py-2.5 !px-6 text-sm"
                >
                  {status === "loading" ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  {status === "success" ? "Gönderildi!" : "Mesaj Gönder"}
                </button>
                {status === "error" && (
                  <p className="text-red-400 text-xs">Bir hata oluştu, tekrar deneyin.</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
